import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'
import type { TaskQueueItem } from '../../lib/tasks'
import type { ReaderContext } from '../../lib/documentReader'
import { formatOfficerValue } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import {
  missingOverrideReasons,
  previewEvidence,
  readEvidenceDecision,
  recordWriteLine,
  submitEvidence,
  type EvidencePreview,
} from './decisions'
import '../canon.css'
import './decisions.css'
import EvidenceRequestActions from './EvidenceRequestActions'

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown })?.detail
    return typeof detail === 'string'
      ? detail
      : 'The evidence review changed. Refresh the current checks.'
  }
  return error instanceof Error ? error.message : 'Could not load this evidence review.'
}
function display(value: unknown): string {
  if (value == null) return 'Unknown'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.map(display).join(', ')
  if (typeof value === 'object') return Object.values(value).map(display).join(' · ')
  return formatOfficerValue(String(value))
}

export function EvidenceCheckRows({
  preview,
  context,
  editing,
  reasons,
  onReason,
  associating = false,
}: {
  preview: EvidencePreview
  context: ReaderContext
  editing: boolean
  reasons: Record<string, string>
  onReason: (key: string, value: string) => void
  associating?: boolean
}) {
  const checks = preview.report.checks
  const needsDecision = (check: (typeof checks)[number]) =>
    check.result === 'fail' || (check.required && check.result === 'unknown')
  const focused = checks.filter(needsDecision)
  const compact = checks.filter((check) => !needsDecision(check))

  const renderCheck = (check: (typeof checks)[number], prominent: boolean) => {
    const accepted = preview.effective_decision?.reasons[check.check_id]
    const failed = check.result === 'fail'
    const requiresReason = needsDecision(check)
    const associated = preview.effective_decision?.resolved_check_ids?.includes(check.check_id)
    const willAssociate = check.check_id === 'object' && associating
    return (
      <section
        className={`ed-check${prominent ? ' ed-check--focus' : ' ed-check--compact'}`}
        key={check.check_id}
        data-check-id={check.check_id}
      >
        {(associated || willAssociate) && (
          <div className="ed-effective">
            <strong>
              {associated
                ? `Document associated by ${preview.effective_decision?.actor?.name ?? 'the officer'}`
                : 'The document will be linked to this movement on acceptance'}
            </strong>
          </div>
        )}
        {accepted && (
          <div className="ed-effective" data-testid="effective-check-decision">
            <strong>
              Accepted by {preview.effective_decision?.actor?.name ?? 'automatic checks'}
            </strong>
            <p>{accepted}</p>
          </div>
        )}
        {editing && requiresReason && !willAssociate && (
          <label className="ed-reason">
            Why accept “{check.label}” despite this result?
            <textarea
              value={reasons[check.check_id] ?? ''}
              required
              rows={2}
              onChange={(event) => onReason(check.check_id, event.target.value)}
              aria-label={`Reason for ${check.label}`}
            />
          </label>
        )}
        <div className={`cn-gt-r${failed ? ' miss' : ''}`} data-original-result={check.result}>
          <span className="st">{check.result === 'pass' ? '✓' : failed ? '✗' : '—'}</span>
          <span className="gate">{check.label}</span>
          <span className="find">
            {check.result === 'pass'
              ? 'Complete'
              : failed
                ? 'Does not pass'
                : check.result === 'not_applicable'
                  ? 'Not applicable'
                  : check.required
                    ? 'Inconclusive'
                    : 'Not observed'}
            {check.detail && <small>{check.detail}</small>}
          </span>
          <span className="vals">
            <span className="lbl">document</span>
            {display(check.asserted)}
            <span className="lbl">record / required</span>
            {display(check.recorded)}
          </span>
          <span className="door">
            {check.source_location?.page && (
              <DocumentLink
                document={{
                  document_id: preview.document_id,
                  display_title: preview.document_title,
                }}
                context={{ ...context, page: check.source_location.page }}
              >
                p. {check.source_location.page}
              </DocumentLink>
            )}
          </span>
        </div>
      </section>
    )
  }

  return (
    <div className="ed-checks" aria-label="Evidence checks">
      {focused.length ? (
        <div className="ed-focused-checks" data-testid="evidence-focused-checks">
          {focused.map((check) => renderCheck(check, true))}
        </div>
      ) : null}
      {compact.length ? (
        <details className="ed-compact-checks">
          <summary>{compact.length} other checks</summary>
          <div className="ed-compact-check-list">
            {compact.map((check) => renderCheck(check, false))}
          </div>
        </details>
      ) : null}
    </div>
  )
}

interface EvidenceDecisionPaneProps {
  task?: TaskQueueItem
  fundId?: string | null
  target?: { kind: string; id: string }
  requirementId: string
  documentId: string
  onResolved: () => void
  onPendingChange?: (pending: boolean) => void
  disabled?: boolean
}

function decisionMatches(
  value: unknown,
  requirementId: string,
  id?: string,
  documentId?: string | null
): boolean {
  if (!value || typeof value !== 'object') return false
  const receipt = value as Record<string, unknown>
  return (
    typeof receipt.id === 'string' &&
    !!receipt.id &&
    (!id || receipt.id === id) &&
    receipt.evidence_status_id === requirementId &&
    (!documentId || receipt.document_id === documentId)
  )
}

export default function EvidenceDecisionPane(props: EvidenceDecisionPaneProps) {
  const session = useAuthSession()
  return (
    <ScopedEvidenceDecisionPane
      key={JSON.stringify([
        session,
        props.requirementId,
        props.documentId,
        props.task?.task_id,
        props.fundId ?? props.task?.fund_id,
        props.target,
      ])}
      {...props}
    />
  )
}

function ScopedEvidenceDecisionPane({
  task,
  requirementId,
  documentId,
  onResolved,
  fundId,
  target,
  onPendingChange,
  disabled = false,
}: EvidenceDecisionPaneProps) {
  const authSession = useAuthSession()
  const [preview, setPreview] = useState<EvidencePreview | null>(null)
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [editing, setEditing] = useState(false)
  const [associate, setAssociate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [gapBusy, setGapBusy] = useState(false)
  const retryKey = useRef<string | null>(null)
  const requestVersion = useRef(0)
  const displayedBinding = useRef<string | null>(null)
  const pendingReceipt = useRef<{ id: string; documentId: string | null } | null>(null)
  useEffect(() => {
    onPendingChange?.(busy || gapBusy || !!pendingId || (!preview && !error))
    return () => onPendingChange?.(false)
  }, [busy, gapBusy, pendingId, preview, error, onPendingChange])
  const scope = (task?.task_data ?? {}) as Record<string, unknown>
  const objectType =
    target?.kind ?? (typeof scope.object_type === 'string' ? scope.object_type : null)
  const objectId = target?.id ?? (typeof scope.object_id === 'string' ? scope.object_id : null)
  const context: ReaderContext = {
    fundId: fundId ?? task?.fund_id,
    tab: 'evidence',
    returnTo: window.location.pathname + window.location.search,
    focus: preview?.association
      ? `position:${preview.association.position_id}`
      : objectType && objectId
        ? `${objectType === 'asset' ? 'entity' : objectType}:${objectId}`
        : undefined,
  }
  const load = useCallback(async () => {
    const version = ++requestVersion.current
    setPreview(null)
    setError(null)
    try {
      const current = await previewEvidence(requirementId, documentId)
      if (requestVersion.current !== version) return
      if (current.evidence_status_id !== requirementId || current.document_id !== documentId)
        throw new Error(
          'The preview belongs to a different requirement or document. Refresh the current review.'
        )
      if (displayedBinding.current !== current.reviewed_binding) {
        setReasons({})
        setEditing(false)
        setAssociate(false)
        retryKey.current = null
      }
      displayedBinding.current = current.reviewed_binding
      setPreview(current)
      if (pendingReceipt.current?.id !== current.pending_decision_id)
        pendingReceipt.current = current.pending_decision_id
          ? { id: current.pending_decision_id, documentId: null }
          : null
      setPendingId(current.pending_decision_id)
      setError(null)
    } catch (failure) {
      if (requestVersion.current === version) setError(errorText(failure))
    }
  }, [requirementId, documentId])
  useEffect(() => {
    displayedBinding.current = null
    retryKey.current = null
    pendingReceipt.current = null
    setPreview(null)
    setPendingId(null)
    setBusy(false)
    setError(null)
    setReasons({})
    setEditing(false)
    setAssociate(false)
    void load()
    return () => {
      requestVersion.current += 1
    }
  }, [load, task?.task_id, authSession])
  useEffect(() => {
    if (!pendingId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const decision = await readEvidenceDecision(pendingId)
        if (cancelled) return
        if (
          !decisionMatches(decision, requirementId, pendingId, pendingReceipt.current?.documentId)
        ) {
          setPendingId(null)
          setPreview(null)
          setError(
            'The decision response does not match this requirement. Refresh the current review.'
          )
          return
        }
        if (decision.outcome === 'pending') {
          timer = setTimeout(poll, 1000)
          return
        }
        retryKey.current = null
        if (decision.outcome === 'applied') {
          // Notify from the verified receipt before clearing pendingId. Clearing
          // it first disposes this polling effect during the awaited refresh
          // and used to suppress the parent's queue/count refresh.
          onResolved()
          setPendingId(null)
          void load()
        } else {
          setPendingId(null)
          setError(decision.detail ?? 'The review changed. Refresh the current checks.')
          setReasons({})
          setEditing(false)
        }
      } catch (failure) {
        if (!cancelled) {
          setError(errorText(failure))
          timer = setTimeout(poll, 2000)
        }
      }
    }
    void poll()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [pendingId, load, onResolved, requirementId])
  const submit = async (action: 'accept' | 'discard') => {
    if (!preview || busy || pendingId || disabled) return
    if (action === 'accept' && preview.required_reason_check_ids.length && !editing) {
      setEditing(true)
      return
    }
    const version = requestVersion.current
    setBusy(true)
    setError(null)
    retryKey.current ??= crypto.randomUUID()
    try {
      const decision = await submitEvidence(
        preview,
        action,
        reasons,
        retryKey.current,
        scope.document_id === documentId ? task?.task_id : undefined,
        associate
      )
      if (requestVersion.current !== version) return
      if (!decisionMatches(decision, requirementId, undefined, documentId)) {
        setPreview(null)
        throw new Error(
          'The response did not confirm a decision for this document and requirement. Refresh before retrying.'
        )
      }
      pendingReceipt.current = { id: decision.id, documentId }
      setPendingId(decision.id)
    } catch (failure) {
      if (requestVersion.current === version) setError(errorText(failure))
    } finally {
      if (requestVersion.current === version) setBusy(false)
    }
  }
  const factualSentence =
    preview?.report.original_summary ||
    task?.title ||
    'Review this document against the evidence requirement.'
  return (
    <div className="cnv ed-pane" data-testid="evidence-decision-card">
      <div className="cn-back" />
      <div className="cn-card">
        <div className="cn-ctit ed-card-band">
          <span className="k verify">Evidence</span>
          <DocumentLink
            className="cn-opensrc"
            document={{ document_id: documentId, display_title: preview?.document_title }}
            context={context}
          />
        </div>
        <p className="ed-factual-sentence">{factualSentence}</p>
        {!preview && !error ? <p role="status">Reading the current evidence checks…</p> : null}
        {preview?.status === 'review_required' && (
          <div className="cn-abar-note" role="status">
            The record or document changed. Review these current checks; the previous decision stays
            in history.
          </div>
        )}
        {preview?.evidence_gap_error && (
          <div className="cn-errline" role="alert">
            {preview.evidence_gap_error}
          </div>
        )}
        {!!preview?.evidence_gap_requirements?.length && (
          <section aria-label="Evidence requests" className="cn-abar-note">
            <strong>Evidence requests</strong>
            {!preview.can_accept && <p>Resolve each open request before signing off.</p>}
            <ul>
              {preview.evidence_gap_requirements.map((request) => (
                <li key={request.id}>
                  <span>{request.title} — </span>
                  {request.status === 'waived' ? (
                    <>
                      <strong>
                        {preview.new_evidence_waiver_ids?.includes(request.id)
                          ? 'New waiver'
                          : 'Waived'}
                      </strong>
                      <p>{request.waiver_reason}</p>
                    </>
                  ) : request.effective_status === 'superseded_requirement' ? (
                    <span>Replaced by a follow-up request</span>
                  ) : request.effective_status === 'fulfilled' ? (
                    <span>Evidence supplied</span>
                  ) : request.effective_status === 'fulfilment_withdrawn' ? (
                    <span>Supplied evidence is no longer available</span>
                  ) : (
                    <span>Open</span>
                  )}
                  {request.status === 'open' && (
                    <EvidenceRequestActions
                      requestId={request.id}
                      context={context}
                      disabled={disabled || busy || gapBusy || !!pendingId}
                      onBusy={setGapBusy}
                      onChanged={() => void load()}
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {preview?.association && !pendingId && (
          <label className="ed-reason">
            <input
              type="checkbox"
              checked={associate}
              onChange={(event) => {
                setAssociate(event.target.checked)
                retryKey.current = null
                setReasons((current) =>
                  Object.fromEntries(Object.entries(current).filter(([key]) => key !== 'object'))
                )
              }}
            />{' '}
            Use this document for this movement
            <small>
              {preview.association.witness_kind === 'authorisation_entities'
                ? 'The document names the same holder and issuer. This association is recorded with your decision.'
                : 'The document is already linked to the same position. This association is recorded with your decision.'}
            </small>
          </label>
        )}
        {preview && (
          <EvidenceCheckRows
            associating={associate}
            preview={preview}
            context={context}
            editing={editing}
            reasons={reasons}
            onReason={(key, value) => {
              retryKey.current = null
              setReasons((current) => ({ ...current, [key]: value }))
            }}
          />
        )}
        {error && (
          <div className="cn-errline" role="alert">
            {error}{' '}
            <button
              type="button"
              className="cn-btn"
              onClick={() => {
                retryKey.current = null
                void load()
              }}
            >
              Refresh
            </button>
          </div>
        )}
        {pendingId ? (
          <div className="cn-abar-note" role="status">
            An evidence decision for this requirement is being applied…
          </div>
        ) : (
          preview &&
          !['satisfied', 'waived', 'not_applicable'].includes(preview.status) && (
            <>
              {!!preview.inconclusive_check_ids.length && (
                <div className="cn-abar-note">Some required comparisons are inconclusive.</div>
              )}
              {!!(preview.record_writes ?? []).length && (
                <div className="ed-record-writes" data-testid="evidence-record-writes">
                  <span className="ed-record-writes-title">Accepting records</span>
                  <ul>
                    {preview.record_writes.map((write) => (
                      <li
                        key={`${write.target_type}:${write.target_id}:${write.field}`}
                        data-record-write-field={write.field}
                      >
                        {recordWriteLine(write, preview.document_title)}
                        {write.detail && <small>{write.detail}</small>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="cn-abar">
                <button
                  className="cn-btn"
                  type="button"
                  disabled={busy || disabled}
                  onClick={() => {
                    retryKey.current = null
                    void submit('discard')
                  }}
                >
                  Discard as evidence
                </button>
                <button
                  className="cn-btn p verify"
                  type="button"
                  disabled={
                    disabled ||
                    busy ||
                    !preview.can_accept ||
                    gapBusy ||
                    (!!preview.association && !associate) ||
                    (editing &&
                      missingOverrideReasons(
                        preview.report.checks.filter(
                          (check) => !(associate && check.check_id === 'object')
                        ),
                        reasons
                      ).length > 0)
                  }
                  onClick={() => void submit('accept')}
                >
                  {editing ? 'Accept with these reasons' : 'Accept'}
                </button>
              </div>
            </>
          )
        )}
        <footer className="ed-documents">
          <span>Documents</span>
          <DocumentLink
            document={{ document_id: documentId, display_title: preview?.document_title }}
            context={context}
          >
            {preview?.document_title ?? 'Open document'} →
          </DocumentLink>
        </footer>
      </div>
    </div>
  )
}
